import { AnimatePresence, motion } from 'framer-motion'
import { useSound } from '../components/SoundHandler'
import svgLogo from '../assets/logo.png'
import { icons } from '../assets/icons/icons';
import { useModal } from '~/core/contexts/ModalContext';
import { atom, useAtom } from 'jotai';
import { useResolution } from '../hooks/useResolution';
import { ProfileSlot } from '~/blinko/components/ProfileSlot'
import { MetadataSlot } from '../components/MetadataHandler'
import { LeaderboardModal } from './leaderboard.modal'
import { useAccount } from 'wagmi'
import { ShopModal } from './shop.modal'
import { ContainerShape, HexagonShape, InnerShape } from '../components/Shape';
import { WalletMenu } from '../components/WalletMenu';
import { SETTINGS } from '../constants';
import { useEffect, useRef } from 'react';
import { useBGM } from '../hooks/useBGM';
import * as RadixSlider from "@radix-ui/react-slider";
import svgVolumeBar from '../assets/volume-bar.svg'
import { formatNumber } from '~/core/utils/formatNumber';

const atomShowHowToPlay = atom(false)

export const MenueModal = ({ onDismiss }: { onDismiss: () => void }) => {
  const sound = useSound();
  const { showModal } = useModal();
  const { isMobile } = useResolution()
  const { address } = useAccount()

  const [showHowToPlay, setShowHowToPlay] = useAtom(atomShowHowToPlay)

  const onClose = () => {
    onDismiss()
    sound.play('click');
  }

  const onHelp = () => {
    setTimeout(() => {
      setShowHowToPlay(!showHowToPlay)
    }, 0);
    sound.play('click');
  }

  const onLeaderboard = () => {
    showModal(hide => <LeaderboardModal onDismiss={hide} />, { hideOnEscapeKey: true });
    sound.play('click');
    onDismiss();
  }

  const onShop = () => {
    showModal(hide => <ShopModal onDismiss={hide} />, { hideOnEscapeKey: true });
    sound.play('click');
    onDismiss();
  }

  if (isMobile) {
    return (
      <div className="fixed z-30 inset-0">
        <motion.div
          className="absolute inset-0 bg-[#0a0523]/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />
  
        <motion.div
          className="absolute inset-0"
          initial={{ backdropFilter: 'blur(20px) opacity(0)' }}
          animate={{ backdropFilter: 'blur(20px) opacity(1)' }}
        />
  
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='relative pb-16 h-full overflow-y-auto'
        >
          <div className='sticky left-0 top-0 right-0 z-10'>
            <div className='flex items-center justify-between px-4 pt-6'>
              <img src={svgLogo} className='absolute left-4 h-14' />
              <div></div>

              <div className='flex gap-2.5'>
                <BGMButton />
                <SoundButton />
                <button onPointerDown={onClose}>
                  <HexagonShape cornerRadius={6} strokeWidth={1}>
                    <div className="flex justify-center items-center p-2">
                      <img src={icons.close} className='w-6 h-6' />
                    </div>
                  </HexagonShape>
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6 px-4 py-6">

            <div className='flex flex-col gap-2'>
              <WalletMenu />

              <div onClick={onLeaderboard}>
                <MetadataSlot>
                  {({ leaderboardMetadata }) => leaderboardMetadata ? (
                    <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={2}>
                      <ProfileSlot address={address}>
                        {({ profile }) => (
                          <div className="flex items-center gap-3 p-4">
                            <HexagonShape cornerRadius={6} strokeWidth={1}>
                              <div className='w-10 h-10 p-2'>
                                <img src={leaderboardMetadata.image} />
                              </div>
                            </HexagonShape>
                            <div className="flex flex-col flex-1 gap-1">
                              <p className="text-left text-[10px] font-blinko text-white/80">
                                Leaderboard place:
                              </p>
                              <div className="flex items-center relative gap-2">
                                <p className="text-base font-blinko text-white">
                                  {leaderboardMetadata.league === 'None' ? '-' : leaderboardMetadata.rank}
                                </p>
                                <div className="rounded" style={{ border: '1px solid #FFF085'}}>
                                  <div className="px-2 py-0.5 bg-[#F0B10080]">
                                    <p className="text-xs font-semibold text-white">
                                      {leaderboardMetadata.title}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                             <button onPointerDown={(e) => {
                              e.stopPropagation()
                              onShop()
                            }}>
                              <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                                <div className='flex justify-center items-center gap-2 px-4 py-2'>
                                  <img src={icons.jars} className='w-4 h-4' />
                                  <p className="font-blinko text-sm text-white">{formatNumber(profile.totalHoney)}</p>
                                </div>
                              </InnerShape>
                            </button>
                          </div>
                        )}
                      </ProfileSlot>
                    </ContainerShape>
                  ) : (
                     <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                        {isMobile ? (
                          <div className="relative p-3">
                            <img src={icons.cup} className='w-5 h-5' />
                            <div className='absolute inset-0 flex items-center justify-center'>
                              <p className="text-base font-medium text-white">Leaderboard</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-center items-center gap-3 px-8 py-3">
                            <img src={icons.cup} className='w-5 h-5' />
                            <p className="text-base font-medium text-white">Leaderboard</p>
                          </div>
                        )}
                    </InnerShape>
                  )}
                </MetadataSlot>
              </div>
            </div>

            <div className='w-full h-[1px] bg-white/10' />

            <div className='flex flex-col gap-2'>

              <a href="https://x.com/bearish_af" target="_blank">
                <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                  <div className="relative p-2.5">
                    <img src={icons.twitterX} className='w-5 h-5' />
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <p className="text-sm font-blinko text-white">@bearish_af</p>
                    </div>
                  </div>
                </InnerShape>
              </a>

              <a href="https://discord.gg/bearish" target="_blank">
                <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                  <div className="relative p-2.5">
                    <img src={icons.brandDiscord} className='w-5 h-5' />
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <p className="text-sm font-blinko text-white">Customer Support</p>
                    </div>
                  </div>
                </InnerShape>
              </a>

              <a href="https://docs.blinko.gg" target="_blank">
                <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                  <div className="relative p-2.5">
                    <img src={icons.help} className='w-5 h-5' />
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <p className="text-sm font-blinko text-white">Docs</p>
                    </div>
                  </div>
                </InnerShape>
              </a>
              
              {/* <div className='relative'>
                <AnnouncementButton />
                <div className='absolute left-0 right-0 bottom-[100%] pb-2'>
                  <AnnouncementPopover />
                </div>
              </div> */}

            </div>
          </div>

        </motion.div>
      </div>
    )
  }

  return (
    <div className="fixed z-40 inset-0">
      <motion.div
        className="absolute inset-0 bg-[#0a0523]/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      <motion.div
        className="absolute inset-0"
        initial={{ backdropFilter: 'blur(20px) opacity(0)' }}
        animate={{ backdropFilter: 'blur(20px) opacity(1)' }}
        onClick={onClose}
      />

      <div className='flex justify-end w-full h-full p-8'>
        <motion.div
          initial={{ x: '10%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 500 }}
          className='relative'
        >          
          <ContainerShape className='w-[406px] h-full' cornerDiagonal={20} cornerRadius={12} strokeWidth={2}>
            <div className="flex flex-col px-6 py-8">

              <div className='flex items-center justify-between mb-8'>
                 {/* <button onPointerDown={onHelp}>
                  <HexagonShape cornerRadius={6} strokeWidth={1}>
                    <div className="flex justify-center items-center p-2">
                      <img src={icons.help} className='w-6 h-6' />
                    </div>
                  </HexagonShape>
                </button> */}
                <img src={svgLogo} alt="logo" className="w-[157px]" />
                <button onPointerDown={onClose}>
                  <HexagonShape cornerRadius={6} strokeWidth={1}>
                    <div className="flex justify-center items-center p-2">
                      <img src={icons.close} className='w-6 h-6' />
                    </div>
                  </HexagonShape>
                </button>
              </div>

              <div className='flex flex-col gap-3'>

                <a href="https://x.com/bearish_af" target="_blank">
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.twitterX} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="text-sm font-blinko text-white">@bearish_af</p>
                      </div>
                    </div>
                  </InnerShape>
                </a>

                 <a href="https://discord.gg/bearish" target="_blank">
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.brandDiscord} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="text-sm font-blinko text-white">Customer Support</p>
                      </div>
                    </div>
                  </InnerShape>
                </a>

                 <a href="https://docs.blinko.gg" target="_blank">
                  <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                    <div className="relative p-2.5">
                      <img src={icons.help} className='w-5 h-5' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <p className="text-sm font-blinko text-white">Docs</p>
                      </div>
                    </div>
                  </InnerShape>
                </a>

              </div>
            </div>
          </ContainerShape>
        </motion.div>
      </div>
    </div>
  )
};



const atomShowBGMPopover = atom(false)

export const BGMButton = () => {
  const sound = useSound()

  const [showPopover, setShowPopover] = useAtom(atomShowBGMPopover)

  const onClick = () => {
    setTimeout(() => {
      setShowPopover(!showPopover)
    }, 0);
    sound.play('click');
  }

  return (
    <div className="relative">
      <button onPointerDown={onClick}>
        <HexagonShape cornerRadius={6} strokeWidth={1}>
          <div className="flex justify-center items-center p-2">
            <img src={icons.music} className='w-6 h-6' />
          </div>
        </HexagonShape>
      </button>
      <BGMPopover />
    </div>
  )
}

const BGMPopover = () => {
  const { volume, setVolume } = useBGM()

  const [showPopover, setShowPopover] = useAtom(atomShowBGMPopover)

  const handleOutsideClickRef = useRef<HTMLDivElement>(null)
  const handleOutsideClick = (event: PointerEvent) => {
    if (handleOutsideClickRef.current && !handleOutsideClickRef.current.contains(event.target as Node)) {
      setShowPopover(false)
    }
  }

  useEffect(() => {
    document.addEventListener('pointerdown', handleOutsideClick)
    return () => document.removeEventListener('pointerdown', handleOutsideClick)
  }, [])

  const onChange = (value: number) => {
    setVolume(value / SETTINGS.MAX_VOLUME)
  }

  return (
    <div className="absolute top-[100%] left-1/2 -translate-x-1/2 mt-2 z-30 w-[38px]">
      <AnimatePresence>
        {showPopover ? (
          <motion.div
            ref={handleOutsideClickRef}
            key="popover"
            initial={{ y: '-20%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 500 }}
          >
            <div 
              ref={handleOutsideClickRef}
              className="relative"
            >
              <img src={svgVolumeBar} className="w-full" />

              <div className="absolute inset-0 flex justify-center items-center">
                <RadixSlider.Root 
                  orientation="vertical"
                  className="relative flex flex-col items-center select-none touch-none w-4 h-[80px]"
                  value={[volume * SETTINGS.MAX_VOLUME]}
                  min={SETTINGS.MIN_VOLUME}
                  max={SETTINGS.MAX_VOLUME}
                  onValueChange={values => onChange(values[0])}
                >
                  <RadixSlider.Track className="relative flex-grow w-full h-full">
                    <div className="absolute left-1/2 -translate-x-1/2 w-1.5 h-[80px] bg-white/10 rounded-full" />
                    <RadixSlider.Range className="absolute bg-[#FDC700] rounded-full w-1 left-1/2 -translate-x-1/2" />
                  </RadixSlider.Track>

                  <RadixSlider.Thumb className="relative block w-5 h-5 focus:outline-none">
                    <img src={icons.paw} className="w-full h-full" />
                  </RadixSlider.Thumb>
                </RadixSlider.Root>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

const atomShowSoundPopover = atom(false)

export const SoundButton = () => {
  const sound = useSound()

  const [showPopover, setShowPopover] = useAtom(atomShowSoundPopover)

  const onClick = () => {
    setTimeout(() => {
      setShowPopover(!showPopover)
    }, 0);
    sound.play('click');
  }

  const volume = sound.sfxVolume * SETTINGS.MAX_VOLUME

  return (
    <div className="relative">
      <button onPointerDown={onClick}>
        <HexagonShape cornerRadius={6} strokeWidth={1}>
          <div className="flex justify-center items-center p-2">
            <img src={volume === SETTINGS.MAX_VOLUME ? icons.volumeFull : volume === SETTINGS.MIN_VOLUME ? icons.volumeOff : icons.volumeLow}  className='w-6 h-6' />
          </div>
        </HexagonShape>
      </button>
      <SoundPopover />
    </div>
  )
}

const SoundPopover = () => {
  const sound = useSound()
  const { isMobile} = useResolution()

  const [showPopover, setShowPopover] = useAtom(atomShowSoundPopover)

  const handleOutsideClickRef = useRef<HTMLDivElement>(null)
  const handleOutsideClick = (event: PointerEvent) => {
    if (handleOutsideClickRef.current && !handleOutsideClickRef.current.contains(event.target as Node)) {
      setShowPopover(false)
    }
  }

  useEffect(() => {
    document.addEventListener('pointerdown', handleOutsideClick)
    return () => document.removeEventListener('pointerdown', handleOutsideClick)
  }, [])

  const onChange = (value: number) => {
    sound.setSfxVolume(value / SETTINGS.MAX_VOLUME)
  }

  return (
    <div className="absolute top-[100%] left-1/2 -translate-x-1/2 mt-2 z-30 w-[38px]">
      <AnimatePresence>
        {showPopover ? (
          <motion.div
            ref={handleOutsideClickRef}
            key="popover"
            initial={{ y: '-20%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 500 }}
          >
            <div 
              ref={handleOutsideClickRef}
              className="relative"
            >
              <img src={svgVolumeBar} className="w-full" />

              <div className="absolute inset-0 flex justify-center items-center">
                <RadixSlider.Root 
                  orientation="vertical"
                  className="relative flex flex-col items-center select-none touch-none w-4 h-[80px]"
                  value={[sound.sfxVolume * SETTINGS.MAX_VOLUME]}
                  min={SETTINGS.MIN_VOLUME}
                  max={SETTINGS.MAX_VOLUME}
                  onValueChange={values => onChange(values[0])}
                >
                  <RadixSlider.Track className="relative flex-grow w-full h-full">
                    <div className="absolute left-1/2 -translate-x-1/2 w-1.5 h-[80px] bg-white/10 rounded-full" />
                    <RadixSlider.Range className="absolute bg-[#FDC700] rounded-full w-1 left-1/2 -translate-x-1/2" />
                  </RadixSlider.Track>

                  <RadixSlider.Thumb className="relative block w-5 h-5 focus:outline-none">
                    <img src={icons.paw} className="w-full h-full" />
                  </RadixSlider.Thumb>
                </RadixSlider.Root>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
